"use client";

import { LucideIcon } from "lucide-react";
import {
  ReactNode,
  forwardRef,
  ComponentType,
  SVGProps,
  ComponentPropsWithoutRef,
  ComponentRef,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";
import { Button, ButtonProps } from "./buttons/button";

export type CreatorDialogProps = ComponentPropsWithoutRef<
  typeof DialogPrimitive.Root
>;

export const CreatorDialog = DialogPrimitive.Root;

export const CreatorDialogTrigger = DialogPrimitive.Trigger;

export const CreatorDialogPortal = DialogPrimitive.Portal;

export const CreatorDialogClose = DialogPrimitive.Close;

const CreatorDialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      className,
    )}
    {...props}
  />
));
CreatorDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type CreatorDialogContentProps = ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
>;

/**
 * Full-screen dialog content wrapper with centered white card
 */
export const CreatorDialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  CreatorDialogContentProps
>(({ className, children, ...props }, ref) => (
  <CreatorDialogPortal>
    <CreatorDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      aria-describedby={undefined}
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed left-2 right-2 top-[50%] z-50 mx-auto flex w-auto max-w-56 translate-y-[-50%] flex-col gap-2 rounded-[16px] bg-white p-2 shadow-lg duration-200",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </CreatorDialogPortal>
));
CreatorDialogContent.displayName = DialogPrimitive.Content.displayName;

export type CreatorDialogHeaderProps = {
  title?: string;
  icon?: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  className?: string;
};

/**
 * Dialog header with optional icon and title - uses DialogPrimitive.Title for accessibility
 */
export const CreatorDialogHeader = ({
  title,
  icon: Icon,
  iconClassName,
  className,
}: CreatorDialogHeaderProps) => (
  <DialogPrimitive.Title asChild>
    <div className={cn("flex items-center justify-center gap-2", className)}>
      {Icon && <Icon className={cn("h-3 w-3 shrink-0", iconClassName)} />}
      <Typography as="h2" size="sm" weight="normal" className="flex-1">
        {title}
      </Typography>
    </div>
  </DialogPrimitive.Title>
);

export type CreatorDialogBodyProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Dialog body content area
 */
export const CreatorDialogBody = ({
  children,
  className,
}: CreatorDialogBodyProps) => (
  <div className={cn("flex flex-col", className)}>{children}</div>
);

export type CreatorDialogFooterProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Dialog footer with left-aligned buttons
 */
export const CreatorDialogFooter = ({
  children,
  className,
}: CreatorDialogFooterProps) => (
  <div className={cn("flex items-center justify-end gap-1", className)}>
    {children}
  </div>
);

export type CreatorDialogButtonProps = Omit<ButtonProps, "size">;

/**
 * Pre-configured button for use in dialog footer
 */
export const CreatorDialogButton = forwardRef<
  HTMLButtonElement,
  CreatorDialogButtonProps
>(({ variant = "filled", ...props }, ref) => (
  <Button ref={ref} size="sm" variant={variant} {...props} />
));
CreatorDialogButton.displayName = "CreatorDialogButton";
